import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types';
import { useUnreadMessages } from '../../contexts/UnreadMessagesContext';
import { 
  LayoutDashboard, 
  MessageSquare, 
  CheckSquare, 
  Layers, 
  Menu, 
  X, 
  Calendar, 
  Timer, 
  Users, 
  Coins, 
  Shield, 
  FolderOpen, 
  Settings, 
  Headphones, 
  BookOpen, 
  ScrollText, 
  Target, 
  Archive, 
  UserCircle,
  Compass
} from 'lucide-react';

export const BottomTabsNav: React.FC = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const { totalUnreadCount } = useUnreadMessages();
    const isDomina = user?.role === UserRole.DOMINA;
    const [isMoreOpen, setIsMoreOpen] = useState(false);

    // Primary Quick Tabs for bottom bar
    const primaryDominaTabs = [
        { to: '/', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard' },
        { to: '/messenger', icon: <MessageSquare className="w-5 h-5" />, label: 'Messenger', badgeCount: totalUnreadCount },
        { to: '/tasks', icon: <CheckSquare className="w-5 h-5" />, label: 'Aufgaben' },
        { to: '/clients', icon: <Users className="w-5 h-5" />, label: 'Klienten' },
    ];

    const primarySubTabs = [
        { to: '/', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard' },
        { to: '/messenger', icon: <MessageSquare className="w-5 h-5" />, label: 'Messenger', badgeCount: totalUnreadCount },
        { to: '/tasks', icon: <CheckSquare className="w-5 h-5" />, label: 'Aufgaben' },
        { to: '/planning', icon: <Layers className="w-5 h-5" />, label: 'Struktur' },
    ];

    const primaryTabs = isDomina ? primaryDominaTabs : primarySubTabs;

    // Full List of Navigation Links for the "Mehr" Sheet
    const dominaGroups = [
        {
            title: 'Übersicht & Klienten',
            links: [
                { to: '/', icon: <LayoutDashboard className="w-4 h-4" />, label: 'Dashboard' },
                { to: '/clients', icon: <Users className="w-4 h-4" />, label: 'Klientenübersicht' },
            ]
        },
        {
            title: 'Aufgaben & Planung',
            links: [
                { to: '/coaching', icon: <Compass className="w-4 h-4" />, label: 'Coaching' },
                { to: '/tasks', icon: <CheckSquare className="w-4 h-4" />, label: 'Aufgaben' },
                { to: '/structure', icon: <Layers className="w-4 h-4" />, label: 'Struktur & Routinen' },
                { to: '/calendar', icon: <Calendar className="w-4 h-4" />, label: 'Kalender' },
                { to: '/timer', icon: <Timer className="w-4 h-4" />, label: 'Timer & Wecker' },
                { to: '/control', icon: <Target className="w-4 h-4" />, label: 'Zielmatrix' },
            ]
        },
        {
            title: 'Interaktion',
            links: [
                { to: '/messenger', icon: <MessageSquare className="w-4 h-4" />, label: 'Messenger', badgeCount: totalUnreadCount },
                { to: '/audio-inputs', icon: <Headphones className="w-4 h-4" />, label: 'Flüsterraum' },
                { to: '/diary', icon: <BookOpen className="w-4 h-4" />, label: 'Tagebuch' },
                { to: '/reports', icon: <ScrollText className="w-4 h-4" />, label: 'Rapporte' },
            ]
        },
        {
            title: 'Verwaltung & Ressourcen',
            links: [
                { to: '/payments', icon: <Coins className="w-4 h-4" />, label: 'Zahlungen' },
                { to: '/rules', icon: <Shield className="w-4 h-4" />, label: 'Regelwerke' },
                { to: '/media', icon: <FolderOpen className="w-4 h-4" />, label: 'Mediathek' },
                { to: '/settings', icon: <Settings className="w-4 h-4" />, label: 'Einstellungen' },
            ]
        }
    ];

    const subGroups = [
        {
            title: 'Übersicht & Aufgaben',
            links: [
                { to: '/', icon: <LayoutDashboard className="w-4 h-4" />, label: 'Dashboard' },
                { to: '/coaching', icon: <Compass className="w-4 h-4" />, label: 'Coaching' },
                { to: '/tasks', icon: <CheckSquare className="w-4 h-4" />, label: 'Meine Aufgaben' },
                { to: '/planning', icon: <Layers className="w-4 h-4" />, label: 'Tagesstruktur' },
                { to: '/calendar', icon: <Calendar className="w-4 h-4" />, label: 'Kalender' },
                { to: '/timer', icon: <Timer className="w-4 h-4" />, label: 'Timer & Wecker' },
            ]
        },
        {
            title: 'Interaktion',
            links: [
                { to: '/messenger', icon: <MessageSquare className="w-4 h-4" />, label: 'Messenger', badgeCount: totalUnreadCount },
                { to: '/audio-inputs', icon: <Headphones className="w-4 h-4" />, label: 'Flüsterraum' },
                { to: '/diary', icon: <BookOpen className="w-4 h-4" />, label: 'Tagebuch' },
                { to: '/reports', icon: <ScrollText className="w-4 h-4" />, label: 'Rapport' },
            ]
        },
        {
            title: 'Regeln & Profil',
            links: [
                { to: '/rules', icon: <Shield className="w-4 h-4" />, label: 'Regeln' },
                { to: '/media', icon: <FolderOpen className="w-4 h-4" />, label: 'Mediathek' },
                { to: '/archive', icon: <Archive className="w-4 h-4" />, label: 'Archiv' },
                { to: '/profile', icon: <UserCircle className="w-4 h-4" />, label: 'Mein Profil' },
                { to: '/settings', icon: <Settings className="w-4 h-4" />, label: 'Einstellungen' },
            ]
        }
    ];

    const groups = isDomina ? dominaGroups : subGroups;

    return (
        <>
            {/* Sliding Drawer for "Mehr" */}
            {isMoreOpen && (
                <div 
                    className="fixed inset-0 bg-black/75 backdrop-blur-md z-40 animate-fade-in"
                    onClick={() => setIsMoreOpen(false)}
                >
                    <div 
                        className="fixed bottom-16 inset-x-0 bg-surface/95 border-t border-border-strong/40 rounded-t-[2rem] p-5 max-h-[82vh] overflow-y-auto shadow-2xl z-50 animate-in slide-in-from-bottom duration-200"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/40">
                            <div>
                                <h2 className="text-base font-bold font-heading text-text-main">Alle Bereiche</h2>
                                <p className="text-xs text-text-secondary">Wähle die gewünschte Ansicht</p>
                            </div>
                            <button 
                                onClick={() => setIsMoreOpen(false)}
                                className="p-2 rounded-xl bg-surface-light hover:bg-surface text-text-main border border-border/40 cursor-pointer"
                                aria-label="Menü schließen"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {groups.map((group, idx) => (
                                <div key={idx}>
                                    <h3 className="text-[10px] font-bold uppercase tracking-wider text-primary mb-2 px-1">
                                        {group.title}
                                    </h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {group.links.map(link => (
                                            <NavLink
                                                key={link.to}
                                                to={link.to}
                                                onClick={() => setIsMoreOpen(false)}
                                                className={({ isActive }) =>
                                                    `p-3 rounded-xl flex items-center gap-2.5 transition-all border ${
                                                        isActive
                                                            ? 'bg-primary/20 border-primary text-text-main font-semibold shadow-glow shadow-primary/10'
                                                            : 'bg-surface-light/40 border-border/40 hover:bg-surface-light text-text-secondary hover:text-text-main'
                                                    }`
                                                }
                                            >
                                                <span className="text-primary shrink-0">{link.icon}</span>
                                                <span className="text-xs truncate">{link.label}</span>
                                                {link.badgeCount !== undefined && link.badgeCount > 0 && (
                                                    <span className="ml-auto bg-rose-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.2 shrink-0">
                                                        {link.badgeCount}
                                                    </span>
                                                )}
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 pt-3 border-t border-border/40 flex justify-between items-center">
                            <button
                                onClick={() => {
                                    setIsMoreOpen(false);
                                    logout();
                                }}
                                className="text-xs font-semibold text-rose-400 hover:text-rose-300 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-950/20 border border-rose-500/20 cursor-pointer"
                            >
                                Abmelden
                            </button>
                            <span className="text-[10px] text-text-secondary truncate max-w-[200px]">{user?.email}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom Tab Bar Container */}
            <nav className="fixed bottom-0 inset-x-0 bg-surface/90 backdrop-blur-2xl border-t border-border/40 h-16 px-2 z-30 flex items-center justify-around shadow-2xl">
                {primaryTabs.map(tab => (
                    <NavLink
                        key={tab.to}
                        to={tab.to}
                        className={({ isActive }) =>
                            `flex flex-col items-center justify-center w-16 py-1 rounded-xl transition-all relative ${
                                isActive ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-main'
                            }`
                        }
                    >
                        <span className="relative">
                            {tab.icon}
                            {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                                <span className="absolute -top-1 -right-2 bg-rose-500 text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center animate-pulse">
                                    {tab.badgeCount > 9 ? '9+' : tab.badgeCount}
                                </span>
                            )}
                        </span>
                        <span className="text-[10px] tracking-wide mt-1 truncate">{tab.label}</span>
                        {location.pathname === tab.to && (
                            <span className="absolute bottom-0 w-8 h-0.5 bg-primary rounded-full shadow-glow"></span>
                        )}
                    </NavLink>
                ))}

                {/* More / Menu Button */}
                <button
                    onClick={() => setIsMoreOpen(prev => !prev)}
                    className={`flex flex-col items-center justify-center w-16 py-1 rounded-xl transition-all cursor-pointer ${
                        isMoreOpen ? 'text-primary font-bold' : 'text-text-secondary hover:text-text-main'
                    }`}
                    aria-label="Weitere Menüpunkte"
                >
                    <Menu className="w-5 h-5" />
                    <span className="text-[10px] tracking-wide mt-1">Mehr</span>
                </button>
            </nav>
        </>
    );
};
